import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TOKENOMICS_SEGMENTS } from "./constants";

export function TokenomicsChart() {
  const [activeIdx, setActiveIdx] = useState(0);

  const cumulativeAngles = TOKENOMICS_SEGMENTS.reduce<number[]>((acc, seg, i) => {
    const prev = i === 0 ? 0 : acc[i - 1];
    acc.push(prev + (seg.pct / 100) * 360);
    return acc;
  }, []);

  const getRotation = (idx: number) => {
    const segStart = idx === 0 ? 0 : cumulativeAngles[idx - 1];
    const segMid = segStart + (TOKENOMICS_SEGMENTS[idx].pct / 100) * 360 / 2;
    return -segMid;
  };

  const r = 80;
  const cx = 100;
  const cy = 100;
  const circ = 2 * Math.PI * r;

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-6">
        {TOKENOMICS_SEGMENTS.map((item, i) => (
          <button
            key={item.label}
            onClick={() => setActiveIdx(i)}
            className={`flex items-center gap-1 text-[13px] px-2.5 py-1 rounded-full border transition-all duration-300 cursor-pointer ${
              activeIdx === i
                ? "border-black/15 shadow-sm"
                : "border-transparent hover:bg-black/[0.03]"
            }`}
            style={activeIdx === i ? { backgroundColor: `${item.color}30` } : {}}
          >
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
            <span className={`font-medium ${activeIdx === i ? "text-black" : "text-black/60"}`}>{item.label}</span>
            <span className="text-black/40">{item.pct}%</span>
          </button>
        ))}
      </div>

      <div className="flex justify-center">
        <div className="relative w-[240px] h-[240px] md:w-[280px] md:h-[280px]">
          <motion.svg
            viewBox="0 0 200 200"
            className="w-full h-full"
            animate={{ rotate: getRotation(activeIdx) - 90 }}
            transition={{ type: "spring", stiffness: 60, damping: 15 }}
          >
            {(() => {
              let offset = 0;
              return TOKENOMICS_SEGMENTS.map((seg, i) => {
                const dash = (seg.pct / 100) * circ;
                const gap = circ - dash;
                const el = (
                  <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={activeIdx === i ? 40 : 34}
                    strokeDasharray={`${dash} ${gap}`}
                    strokeDashoffset={-offset}
                    opacity={activeIdx === i ? 1 : 0.75}
                    className="transition-all duration-500 cursor-pointer"
                    onClick={() => setActiveIdx(i)}
                    style={{ filter: activeIdx === i ? "brightness(1.05)" : "none" }}
                  />
                );
                offset += dash;
                return el;
              });
            })()}
          </motion.svg>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="font-space-grotesk text-4xl md:text-5xl font-bold text-black">1B</div>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIdx}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.25 }}
              className="absolute top-2 left-1/2 -translate-x-1/2 bg-white text-black text-sm font-bold px-4 py-1.5 rounded-lg shadow-md border border-black/[0.08]"
            >
              {TOKENOMICS_SEGMENTS[activeIdx].tokens}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
